// src/pages/en/MainPage_en.js 
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import SupportPopup from "../../components/SupportPopup";
import SupportSection from "../../components/SupportSection";
import "../MainPage.css";

function MainPage_en() {
  const location = useLocation();
  const pathname = location?.pathname || "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const {
    currentLocale,
    prefix,
    canonicalUrl,
    htmlLang,
    ogLocale,
    inLanguage,
    altPaths,
  } = useMemo(() => {
    const firstSeg = pathname.split("/").filter(Boolean)[0];
    const supported = ["en", "ja", "tw"];
    const locale = supported.includes(firstSeg) ? firstSeg : "en";

    const pref = locale === "en" ? "/en" : `/${locale}`;

    const segments = pathname.split("/").filter(Boolean);
    const suffixSegments = supported.includes(segments[0])
      ? segments.slice(1)
      : segments;
    const suffixPath = "/" + suffixSegments.join("/");
    const normalizedSuffix = suffixPath === "//" ? "/" : suffixPath;

    const alt = {
      ko: normalizedSuffix,
      en: "/en" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      ja: "/ja" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      tw: "/tw" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
    };

    const htmlLangMap = { ko: "ko", en: "en", ja: "ja", tw: "zh-TW" };
    const ogLocaleMap = { ko: "ko_KR", en: "en_US", ja: "ja_JP", tw: "zh_TW" };
    const inLangMap = { ko: "ko-KR", en: "en", ja: "ja", tw: "zh-TW" };

    return {
      currentLocale: locale,
      prefix: pref,
      canonicalUrl: (origin || "") + pathname,
      htmlLang: htmlLangMap[locale],
      ogLocale: ogLocaleMap[locale],
      inLanguage: inLangMap[locale],
      altPaths: alt,
    };
  }, [pathname, origin]);

  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "TooLoves",
      description: "Quickly access frequently used tools in one place",
      inLanguage,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl, inLanguage, origin]);

  const alternateLinks =
    origin &&
    [
      <link key="ko" rel="alternate" hrefLang="ko" href={origin + altPaths.ko} />,
      <link key="en" rel="alternate" hrefLang="en" href={origin + altPaths.en} />,
      <link key="ja" rel="alternate" hrefLang="ja" href={origin + altPaths.ja} />,
      <link key="tw" rel="alternate" hrefLang="zh-TW" href={origin + altPaths.tw} />,
      <link key="xdef" rel="alternate" hrefLang="x-default" href={origin + altPaths.en} />,
    ];

  return (
    <div className="main-wrap">
      <SupportPopup locale="en" />
      <Helmet htmlAttributes={{ lang: htmlLang }}>
        <title>TooLoves</title>
        <meta name="description" content="Quickly access frequently used tools in one place" />
        <meta
          name="keywords"
          content="TooLoves, Tool collection, QR, QR code generator, Base64, Watermark, Watermark generator, EXIF, Metadata extraction, AI image, Free, Web utilities, Blog, Web development"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ffffff" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="TooLoves" />
        <meta
          property="og:description"
          content="Quickly access frequently used tools in one place"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-main.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TooLoves" />
        <meta
          name="twitter:description"
          content="Quickly access frequently used tools in one place"
        />
        <meta name="twitter:image" content="/og-main.png" />

        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {alternateLinks}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLd }}
        />

        <style>
          {
            `.lang-switcher {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
            }
            .lang-switcher .lang-btn {
              color: inherit;
              opacity: 0.7;
              text-decoration: none;
              padding: 4px 8px;
              border-radius: 8px;
              transition: opacity .15s, background-color .15s;
            }
            .lang-switcher .lang-btn:hover {
              opacity: 1;
              background: rgba(0,0,0,0.06);
            }
            .lang-switcher .lang-btn.active {
              opacity: 1;
              font-weight: 600;
              background: rgba(0,0,0,0.08);
            }
            .lang-switcher .lang-sep {
              opacity: 0.4;
              user-select: none;
            }
            @media (prefers-color-scheme: dark) {
              .lang-switcher .lang-btn:hover {
                background: rgba(255,255,255,0.08);
              }
              .lang-switcher .lang-btn.active {
                background: rgba(255,255,255,0.12);
              }
            }`
          }
        </style>
      </Helmet>

      <header className="main-hero">
        <nav className="main-nav">
          <div className="nav-left"></div>
          <div className="nav-right">
            <div className="lang-switcher" role="group" aria-label="Language selection">
              {["ko", "en", "ja", "tw"].map((lang, i, arr) => (
                <React.Fragment key={lang}>
                  <Link
                    to={altPaths[lang]}
                    className={`lang-btn ${currentLocale === lang ? "active" : ""}`}
                    lang={lang === "tw" ? "zh-TW" : lang}
                    aria-current={currentLocale === lang ? "page" : undefined}
                  >
                    {lang === "ko"
                      ? "한국어"
                      : lang === "en"
                      ? "English"
                      : lang === "ja"
                      ? "日本語"
                      : "繁體中文"}
                  </Link>
                  {i < arr.length - 1 && (
                    <span className="lang-sep" aria-hidden="true">
                      /
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </nav>

        <div className="hero-content">
          <h1 className="hero-title">TooLoves</h1>
          <p className="hero-subtitle">Quickly access frequently used tools in one place</p>
        </div>
      </header>

      <main className="services">
        <SupportSection locale="en" />
        <h2 className="section-title">Our Services</h2>
        <div className="service-grid">
          {/* QR Code Generator */}
          <article className="service-card">
            <div className="service-icon service-icon-qr" aria-hidden="true">
              <svg viewBox="0 0 64 64" width="48" height="48" role="img">
                <rect x="6" y="6" width="18" height="18" rx="2" />
                <rect x="40" y="6" width="18" height="18" rx="2" />
                <rect x="6" y="40" width="18" height="18" rx="2" />
                <rect x="26" y="26" width="12" height="12" />
              </svg>
            </div>
            <h3 className="service-name">QR Code</h3>
            <p className="service-desc">Convert web URLs into QR codes and save as images</p>
            <Link to={`${prefix}/qr`} className="btn-primary">
              Go to QR Generator
            </Link>
          </article>

          {/* Image Watermark */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🖼️</div>
            <h3 className="service-name">Add Watermark to Images</h3>
            <p className="service-desc">Create your own watermark on images</p>
            <Link to={`${prefix}/watermark`} className="btn-primary">
              Go to Watermark Generator
            </Link>
          </article>

          {/* AI Photo EXIF */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🌀</div>
            <h3 className="service-name">NovelAI Photo EXIF Extractor</h3>
            <p className="service-desc">Easily extract metadata from NovelAI images</p>
            <Link to={`${prefix}/aiexif`} className="btn-primary">
              Go to EXIF Extractor
            </Link>
          </article>

          {/* Base64 Encoder/Decoder */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🔤</div>
            <h3 className="service-name">Base64 Encoder/Decoder</h3>
            <p className="service-desc">Encode or decode text to Base64</p>
            <Link to={`${prefix}/base64`} className="btn-primary">
              Go to Base64 Converter
            </Link>
          </article>
        </div>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="Legal links">
          <Link to={`${prefix}/terms`} className="footer-link">
            Terms of Service
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to={`${prefix}/privacy`} className="footer-link">
            Privacy Policy
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <a
            href="https://tooloves.net/blog"
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Blog
          </a>
        </nav>
      </footer>
    </div>
  );
}

export default MainPage_en;
